<template>
  <v-row class="mb-4 me-0">
    <v-card-title> Caixas </v-card-title>
    <v-spacer />
    <v-btn icon color="primary" @click="add">
      <v-icon icon="fas fa-plus" />
    </v-btn>
  </v-row>
  <v-form
    v-for="(model, index) in models"
    ref="form"
    :key="index"
    :disabled="$loader.loading"
  >
    <v-row>
      <v-col v-if="$can('delete', 'pos')" cols="1">
        <v-btn icon color="error" @click="destroy(model)">
          <v-icon icon="fas fa-trash-can" />
        </v-btn>
      </v-col>
      <v-col :cols="$can('delete', 'pos') ? 2 : 3">
        <v-text-field
          v-model="model.name"
          variant="outlined"
          label="Nome"
          :rules="[rules.required]"
        />
      </v-col>
      <v-col cols="2">
        <v-select
          v-model="model.active"
          :items="status"
          item-text="title"
          item-value="value"
          variant="outlined"
          label="Status"
          :rules="[rules.boolean]"
        />
      </v-col>
      <v-col cols="5">
        <model-picker
          v-model="model.sellers"
          :items="items"
          item-value="id"
          item-title="name"
          variant="outlined"
          label="Vendedores"
          :rules="[rules.required]"
        />
      </v-col>
      <v-col cols="2">
        <v-btn
          color="primary"
          block
          :loading="$loader.loading"
          size="x-large"
          :disabled="!isDirty(model) && !isNew(model)"
          @click="isNew(model) ? create(model) : update(model)"
        >
          {{ isNew(model) ? 'Criar' : 'Salvar' }}
        </v-btn>
      </v-col>
    </v-row>
  </v-form>
</template>

<script setup lang="ts">
import { onMounted, ref, useTemplateRef } from 'vue'
import { useSnackBar } from '@/stores/snackbar'
import type { VForm } from 'vuetify/lib/components'
import SaleTerminal from '@/types/SaleTerminal'
import SaleTerminalService from '@/services/SaleTerminalService'
import { useLoader } from '@/stores/loader'
import PointOfSale from '@/types/PointOfSale'
import ModelPicker from '@/components/form/ModelPicker.vue'
import { useFilters } from '@/helpers/reports/filters'
import { useProducerStore } from '@/stores/producer'
import { useDialog } from '@/stores/dialog'
import { rules } from '@/helpers/rules'
import { useDirtyTracker } from '@/helpers/dirty'

// Interfaces

interface Props {
  pos: PointOfSale
}

// Statics

const status = [ { title: 'Ativo', value: true }, { title: 'Inativo', value: false } ]

const service = new SaleTerminalService()

// Composables

const dialog = useDialog()
const loader = useLoader()
const snackbar = useSnackBar()
const { track, resetTracking, unmarkAsNew, markAsNew, isDirty, isNew } = useDirtyTracker<SaleTerminal>()

const { items } = useFilters().admin(
  useProducerStore().producer!,
)

// Props

const { pos } = defineProps<Props>()

// Refs

const models = ref<SaleTerminal[]>([] as SaleTerminal[])

const forms = useTemplateRef<VForm[]>('form')

// Functions

async function validate(): Promise<boolean> {
  if (!forms.value) return false

  const validations = forms.value.map(f => f.validate())

  const results = await Promise.all(validations)

  if (!results.every(r => r.valid)) {
    snackbar.show(
      'Verifique se os campos e cargos foram preenchidos corretamente',
      'warning',
    )

    return false
  }

  return true
}

async function fetch() {
  try {
    loader.increment()

    const response = service.index(1, `point-of-sale=${pos.id}`, 20)

    models.value = (await response).data.data

    models.value.forEach(v => track(v))
  } finally {
    loader.decrement()
  }
}

async function update(model: SaleTerminal) {
  if (!isDirty(model)) return

  if (!await validate()) return

  try {
    loader.increment()
    await service.update(model.id, model)
    snackbar.show('Caixa atualizado', 'success')
    resetTracking()
    models.value.forEach(m => track(m))
  } finally {
    loader.decrement()
  }
}

async function create(model: SaleTerminal) {
  if (!await validate()) return

  try {
    loader.increment()
    await service.store(model)
    snackbar.show('Caixa atualizado', 'success')
    resetTracking()
    unmarkAsNew(model)
    models.value.forEach(m => track(m))
  } finally {
    loader.decrement()
  }
}

function add() {
  const item = {
    id: '',
    point_of_sale_id: pos.id,
    name: 'Caixa Novo',
    active: true,
    sellers: [],
  }

  markAsNew(item)

  models.value.push(item)
}

async function destroy(model: SaleTerminal) {
  const confirm = await dialog.open({
    title: 'Confirmação de exclusão',
    message: 'Realmente deseja excluir o terminal? Essa ação é irreversível',
  })

  if (!confirm) return

  try {
    loader.increment()

    if (!isNew(model)) {
      await service.destroy(model)
    }

    snackbar.show('Caixa excluido', 'success')
    models.value = models.value.filter(m => m.id !== model.id)
    resetTracking()
    models.value.forEach(m => track(m))
  } finally {
    loader.decrement()
  }
}

onMounted(() => fetch())
</script>
